// REACT IMPORTS
import React, { useEffect, useRef } from "react";

// COMPONENT IMPORTS
import { GOOGLE_CLIENT_ID } from "store/api/constApi";

const GSI_SCRIPT_SRC = "https://accounts.google.com/gsi/client";

declare global {
  interface Window {
    google?: any;
  }
}

const loadGsiScript = () =>
  new Promise<void>((resolve, reject) => {
    if (window.google?.accounts?.oauth2) {
      resolve();
      return;
    }
    const existing = document.querySelector(
      `script[src="${GSI_SCRIPT_SRC}"]`
    ) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () =>
        reject(new Error("Failed to load Google Identity script"))
      );
      return;
    }
    const script = document.createElement("script");
    script.src = GSI_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error("Failed to load Google Identity script"));
    document.head.appendChild(script);
  });

const GoogleGIcon = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
    />
    <path
      fill="#FBBC05"
      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    />
  </svg>
);

interface GoogleLoginButtonProps {
  onCredential: (credential: string) => void;
  text?: "signin_with" | "signup_with" | "continue_with" | "signin";
  "data-testid"?: string;
}

const BUTTON_LABELS: Record<string, string> = {
  signin_with: "Sign in with Google",
  signup_with: "Sign up with Google",
  continue_with: "Continue with Google",
  signin: "Sign in",
};

/* ------------------------------------- GOOGLE LOGIN BUTTON ------------------------------------- */

// A full-width button styled like the portal's own buttons. Google's
// rendered button is an iframe capped at 400px whose clicks are ignored
// when it is hidden or covered, so a custom button cannot wrap it.
// Instead the click opens Google's OAuth popup (token client); the
// resulting access token is verified server-side by /portal/google-login
// just like an ID token credential.
const GoogleLoginButton = ({
  onCredential,
  text = "signin_with",
  "data-testid": dataTestId,
}: GoogleLoginButtonProps) => {
  const tokenClientRef = useRef<any>(null);
  // Google's token client is created once; keep the latest handler in a
  // ref so re-renders don't recreate it.
  const onCredentialRef = useRef(onCredential);
  onCredentialRef.current = onCredential;

  // Preload the script so the click handler can open the popup without
  // losing the user-gesture activation to a slow network fetch.
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) {
      return;
    }
    loadGsiScript().catch((err) => {
      console.log("Google login init error =====", err);
    });
  }, []);

  const onClick = async () => {
    try {
      await loadGsiScript();
      if (!window.google?.accounts?.oauth2) {
        return;
      }
      if (!tokenClientRef.current) {
        tokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: "openid email profile",
          callback: (response: any) => {
            if (response?.access_token) {
              onCredentialRef.current(response.access_token);
            }
          },
          error_callback: (error: any) => {
            // Popup closed or blocked; leave the form untouched.
            console.log("Google login popup error =====", error?.type);
          },
        });
      }
      tokenClientRef.current.requestAccessToken();
    } catch (err) {
      console.log("Google login error =====", err);
    }
  };

  if (!GOOGLE_CLIENT_ID) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center justify-center gap-2 px-2 lg:px-4 lg:py-2.5 py-1.5 rounded-md text-base leading-6 font-medium border border-strokePrimary bg-btnOutlineActive hover:bg-btnOutlineHover text-txtParagraph cursor-pointer select-none focus:outline-none"
      data-testid={dataTestId}
      aria-label={BUTTON_LABELS[text] || BUTTON_LABELS.signin_with}
    >
      <GoogleGIcon />
      {BUTTON_LABELS[text] || BUTTON_LABELS.signin_with}
    </button>
  );
};

export default GoogleLoginButton;
